import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faDownload } from '@fortawesome/free-solid-svg-icons';
import './approvalPage.css';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../utils/axiosInstance';
import { toast } from 'react-toastify';
import { Spinner, Alert } from 'react-bootstrap';

const AttendanceRequests = ({ adminIds, empRole }) => {
  const { t } = useTranslation();

  useEffect(() => {
    // console.log("Employee IDs managed by the logged-in user:", adminIds);
  }, [adminIds]);

  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [downloadMessages, setDownloadMessages] = useState({}); // State to manage download messages
  const [loading, setLoading] = useState(true); // Loading state

  useEffect(() => {
    setLoading(true);
    const fetchApprovalRequests = async () => {
      try {
        const response = await axiosInstance.get('/admin/approvalRequests');
        let filteredRequests;

        if (empRole === 'SuperAdmin') {
          // Set all requests for SuperAdmin, excluding 'Withdrawn'
          filteredRequests = response.data.filter(request => request.status !== 'Withdrawn');
        } else if (adminIds.length > 0) {
          // Filter requests based on adminIds and exclude 'Withdrawn'
          filteredRequests = response.data.filter(request =>
            adminIds.includes(parseInt(request.emp_id)) && request.status !== 'Withdrawn');
        } else {
          filteredRequests = [];
        }
        setRequests(filteredRequests);
      } catch (error) {
        // Check if error response is available
        if (!error.response) {
          // Network error or no response
          toast.error("Looks like you are offline. Please check your internet connectivity.");
        } else {
          console.error('Error fetching approval requests:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchApprovalRequests();
  }, [adminIds, empRole]); // Add empRole as a dependency

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const filteredRequests = requests.filter(request => {
    const matchesFilter = filter === 'All' || request.status === filter;
    const matchesSearchTerm = request.p_name && request.p_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStartDate = startDate === '' || new Date(request.submitted_on).setHours(0, 0, 0, 0) >= new Date(startDate).setHours(0, 0, 0, 0);
    const matchesEndDate = endDate === '' || new Date(request.submitted_on).setHours(0, 0, 0, 0) <= new Date(endDate).setHours(0, 0, 0, 0);

    return matchesFilter && matchesSearchTerm && matchesStartDate && matchesEndDate;
  });

  const handleAccept = (id) => {
    axiosInstance.post(`/admin/approvalRequests/approve/${id}`)
      .then(response => {
        if (response.status === 200) {
          const updatedRequests = requests.map(request => {
            if (request.approvals_id === id) {
              return { ...request, status: 'Approved', approved_date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) };
            }
            return request;
          });
          setRequests(updatedRequests);
        }
      })
      .catch(error => {
        console.error('Error approving approval request:', error);
      });
  };

  const handleDecline = (id) => {
    axiosInstance.post(`/admin/approvalRequests/decline/${id}`)
      .then(response => {
        if (response.status === 200) {
          const updatedRequests = requests.map(request => {
            if (request.approvals_id === id) {
              return { ...request, status: 'Declined' };
            }
            return request;
          });
          setRequests(updatedRequests);
        }
      })
      .catch(error => {
        console.error('Error declining approval request:', error);
      });
  };

  const handleDownload = async (id) => {
    try {
      setDownloadMessages(prevMessages => ({ ...prevMessages, [id]: 'Checking document...' }));

      const filenameResponse = await axiosInstance.get(`/user/getApprovalFilename/${id}`);
      if (filenameResponse.status !== 200) {
        throw new Error('Failed to fetch filename.');
      }
      const filename = filenameResponse.data.originalFilename;

      if (!filename) {
        throw new Error('Filename is null or undefined.');
      }

      const fileResponse = await axiosInstance.get(`/user/downloadApprovalFile/${id}`, {
        responseType: 'blob'
      });

      if (fileResponse.data.size === 0) {
        throw new Error('File is empty.');
      }

      const blob = new Blob([fileResponse.data], { type: fileResponse.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      setDownloadMessages(prevMessages => ({ ...prevMessages, [id]: 'Downloading document' }));

      // Clear the message after 3 seconds
      setTimeout(() => {
        setDownloadMessages(prevMessages => {
          const newMessages = { ...prevMessages };
          delete newMessages[id];
          return newMessages;
        });
      }, 3000);
    } catch (error) {
      setDownloadMessages(prevMessages => ({ ...prevMessages, [id]: 'No Document submitted' }));

      // Clear the message after 3 seconds
      setTimeout(() => {
        setDownloadMessages(prevMessages => {
          const newMessages = { ...prevMessages };
          delete newMessages[id];
          return newMessages;
        });
      }, 3000);
      console.error('Error downloading file:', error.message);
    }
  };

  return (
    <div className="approval-requests">
      <h2 className='pgTitle'>{t("approval_title")}</h2>
      <div className="filter-container-approval">
        <select id="filter" value={filter} onChange={handleFilterChange}>
          <option value="All">{t("approval_all")}</option>
          <option value="Pending">{t("Pending")}</option>
          <option value="Approved">{t("Approved")}</option>
          <option value="Declined">{t("Declined")}</option>
          <option value="Withdrawn">{t("Withdrawn")}</option>
        </select>
        <input
          type="text"
          placeholder={t("approval_search")}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <div style={{ fontSize: '12px', fontWeight: '500' }}>{t("from")}:</div>
        <input
          type="date"
          placeholder={t("leaveRequest_startDate")}
          value={startDate}
          onChange={handleStartDateChange}
        />
        <div style={{ fontSize: '12px', fontWeight: '500' }}>{t("to")}:</div>
        <input
          type="date"
          placeholder={t("leaveRequest_endDate")}
          value={endDate}
          onChange={handleEndDateChange}
        />
      </div>
      <div className='tableContainer' style={{ overflowX: 'auto' }}>
        <table style={{ whiteSpace: 'nowrap' }}>
          <thead>
            <tr>
              <th>{t("projectDetails_EmployeeID")}</th>
              <th>{t("attendance_name")}</th>
              <th>{t("approval_type")}</th>
              <th>{t("approval_reason")}</th>
              <th>{t("approval_submitted")}</th>
              <th>{t("approval_status")}</th>
              <th>{t("approval_document")}</th>
              <th>{t("approval_action")}</th>
            </tr>
          </thead>
          <tbody style={{ background: '#fff' }}>
            {loading ? (
              <tr>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
                <td colSpan={2} className="employee-table-Spinner centered">
                  <Spinner animation="border" role="status">
                    <span className=""></span>
                  </Spinner>
                  <span className="mt-2">{t('Please wait your data is loading')}</span>
                </td>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
                <td className='table-data-row'></td>
              </tr>
            ) : filteredRequests.length === 0 ? (
              <tr>
                <td colSpan="8" className="centered">
                  <Alert variant="light" className='employee-table-Alert'>
                    {t("No employees found")}
                  </Alert>
                </td>
              </tr>
            ) : (
              filteredRequests.map(request => (
                <tr key={request.approvals_id}>
                  <td className='A-info'>{request.emp_sap_id}</td>
                  <td className='A-info'>{request.p_name}</td>
                  <td className='A-info'>{request.approval_type}</td>
                  <td className='A-info'>{request.reason}</td>
                  <td className='A-info'>{new Date(request.submitted_on).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td className={`A-info status-${request.status.toLowerCase()}`}>{t(`status_${request.status.toLowerCase()}`)}</td>
                  <td className='colDownBtn'>
                    <button className='downBtns' onClick={() => handleDownload(request.approvals_id)}>
                      <FontAwesomeIcon icon={faDownload} />
                    </button>
                    {downloadMessages[request.approvals_id] && (
                      <p className="text-danger mt-2 mb-0">{downloadMessages[request.approvals_id]}</p>
                    )}
                  </td>
                  <td className='A-info'>
                    {request.status === 'Pending' && (
                      <>
                        <button className="approveButton" onClick={() => handleAccept(request.approvals_id)}>
                          <FontAwesomeIcon icon={faCheck} />
                        </button>
                        <button className="declineButton" onClick={() => handleDecline(request.approvals_id)}>
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default AttendanceRequests;
